import React, { useContext, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Button,
  Center,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Flex,
  Input,
  Spacer,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import Auth from '@aws-amplify/auth';
import Router from 'next/router';
import withLogger, { Logger } from '../libs/logger';
import { passwordRegExp } from '../libs/validation';
import { ForgotPassword } from '../libs/authentication';
import { AuthContext } from '../context/authContext';
import { EventStateContext } from '../context/EventContext';
import { AccessLevel, Action, Category } from '../libs/analytics';
import { EnhancedAnalyticsContext } from '../context/enhancedAnalyticsContext';

type ResetPasswordFormProps = {
  user: {
    email: string;
  };
  callback(err: string | null, password?: string): void;
  log: Logger;
};

function ResetPasswordForm({ log, callback, user }: ResetPasswordFormProps) {
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const { slug } = useContext(EventStateContext);
  const { setUserSession } = useContext(AuthContext as any);
  const { saveAnalytics } = useContext(EnhancedAnalyticsContext as any);

  const errorMessage = error.length > 0 && (
    <Alert status='error'>
      <AlertIcon />
      {error}
    </Alert>
  );

  const sentMessage = verificationSent && (
    <Alert status='success'>
      <AlertIcon />
      An email with a verification code has been sent to
      {user.email}
    </Alert>
  );

  function onCancel() {
    setError('');

    callback(null);
  }

  async function onNotReceived() {
    setError('');

    // try to send verification code
    const codeSent = await ForgotPassword({ username: `${user.email}#/${slug.slice(0, -1)}` }).catch((err) => {
      log.error({ action: 'onNotReceived:ForgotPassword:error', error: err });
      setError(err.message);
      return false;
    });

    setVerificationSent(codeSent);
  }

  async function onResetPassword(evt: any) {
    const { password, code } = evt;

    setError('');

    // try to reset password
    const passwordReset = await Auth.forgotPasswordSubmit(`${user.email}#/${slug.slice(0, -1)}`, code, password).catch(
      (err) => {
        log.error({ action: 'onResetPassword:Auth.forgotPasswordSubmit:error', error: err });
        setError(err.message);
        return false;
      }
    );

    // returned if not verified
    if (passwordReset === false) {
      return;
    }

    // try to auto sign in
    const signedIn = await Auth.signIn(`${user.email}#/${slug.slice(0, -1)}`, password).catch((e) => {
      log.error({ action: 'onResetPassword:Auth.signIn:error', error: e });
      setError('Could not sign in.');
      return false;
    });

    // return if could not sign in
    if (signedIn === false) {
      return;
    }

    await setUserSession(
      user.email.slice(0, user.email.indexOf('@')),
      ' ',
      `${user.email}#/${slug.slice(0, -1)}`,
      AccessLevel.REGISTRATION
    );
    saveAnalytics(Action.PASSWORD_RESET, Category.USER_EVENT, 'changePassword');

    // Route to account on successful login
    await Router.push(
      (Router.router.query &&
        Router.router.query.page &&
        JSON.stringify(Router.router.query.page).toString().replaceAll('"', '')) ||
        '/#/westcon_comstor/home'
    ).catch((e) => {
      log.error({ action: 'onResetPassword:router:error', error: e });
      setError('Could not load account page');
    });
  }

  const onResetPasswordSchema = yup.object({
    email: yup.string().email('Must be a valid email').required('Email is required'),
    code: yup
      .string()
      .matches(/^\d{6}$/, 'Not a valid code')
      .required('Verification code is required'),
    password: yup
      .string()
      .matches(passwordRegExp, {
        message: (cur) => {
          const fails: string[] = [];
          if (!cur.value.match(/[a-z]/)) {
            fails.push('lowercase');
          }
          if (!cur.value.match(/[A-Z]/)) {
            fails.push('uppercase');
          }
          if (!cur.value.match(/[0-9]/)) {
            fails.push('number');
          }
          if (!cur.value.match(/[!@#$%^&*]/)) {
            fails.push('special character');
          }
          return `Password must include ${fails.join(', ')}`;
        }
      })
      .min(8, 'Minimum length is 8 characters')
      .required('New password is required'),
    passwordConfirm: yup
      .string()
      .oneOf([yup.ref('password'), null], 'Passwords must match')
      .required('Confirm password is required')
  });

  return (
    <>
      <Center>
        <Text mb='8' color={useColorModeValue('gray.600', 'whiteAlpha.600')}>
          <p>Reset your password using the verification code received via email</p>
        </Text>
      </Center>
      <Formik
        validationSchema={onResetPasswordSchema}
        onSubmit={onResetPassword}
        initialValues={{
          email: user.email,
          code: '',
          password: '',
          passwordConfirm: ''
        }}
      >
        {({ isSubmitting, handleSubmit, handleChange, handleBlur, values, touched, errors }) => (
          <Form noValidate onSubmit={handleSubmit} color='gray.400'>
            {errorMessage}
            {sentMessage}
            <Field name='email'>
              {({ field, form }) => (
                <FormControl mb='8' isInvalid={form.errors.email && form.touched.email}>
                  <FormLabel htmlFor='email' srOnly>
                    Email
                  </FormLabel>
                  <Input
                    {...field}
                    id='email'
                    placeholder='email'
                    bg={useColorModeValue('gray.300', 'whiteAlpha.400')}
                    colorScheme='whiteAlpha'
                  />
                  <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Field name='code'>
              {({ field, form }) => (
                <FormControl mb='8' isInvalid={form.errors.code && form.touched.code}>
                  <FormLabel htmlFor='code' srOnly>
                    Verification Code
                  </FormLabel>
                  <Input
                    {...field}
                    id='code'
                    placeholder='Email verification code'
                    bg={useColorModeValue('gray.300', 'whiteAlpha.400')}
                    colorScheme='whiteAlpha'
                  />
                  <FormErrorMessage>{form.errors.code}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Field name='password'>
              {({ field, form }) => (
                <FormControl mb='8' isInvalid={form.errors.password && form.touched.password}>
                  <FormLabel htmlFor='password' srOnly>
                    New Password
                  </FormLabel>
                  <Input
                    {...field}
                    id='password'
                    placeholder='Password'
                    type='password'
                    bg={useColorModeValue('gray.300', 'whiteAlpha.400')}
                    colorScheme='whiteAlpha'
                  />
                  <FormErrorMessage>{form.errors.password}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Field name='passwordConfirm'>
              {({ field, form }) => (
                <FormControl mb='8' isInvalid={form.errors.password && form.touched.password}>
                  <FormLabel htmlFor='passwordConfirm' srOnly>
                    Confirm Password
                  </FormLabel>
                  <Input
                    {...field}
                    id='passwordConfirm'
                    placeholder='Confirm password'
                    type='password'
                    bg={useColorModeValue('gray.300', 'whiteAlpha.400')}
                    colorScheme='whiteAlpha'
                  />
                  <FormErrorMessage>{form.errors.password}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Flex mb='8'>
              <Spacer />
              <Button variant='link' colorScheme='brand' onClick={onCancel}>
                Cancel
              </Button>
            </Flex>
            <Flex mb='8'>
              <Spacer />
              <Button variant='link' colorScheme='brand' onClick={onNotReceived}>
                Didn&apos;t receive your verification code?
              </Button>
            </Flex>
            <Button width='100%' type='submit' borderRadius='2' colorScheme='brand' isLoading={isSubmitting}>
              Change password
            </Button>
          </Form>
        )}
      </Formik>
    </>
  );
}

export default withLogger(ResetPasswordForm, 'component:ResetPasswordForm');
